import { InputGroup } from "@/components/base/input/input-group";
import { AddressInput } from "@/components/form-input/address-input";
import { DateInput } from "@/components/form-input/date-input";
import { EmailInput } from "@/components/form-input/email-input";
import { FirstNameInput } from "@/components/form-input/first-name-input";
import { GenderInput } from "@/components/form-input/gender-input";
import { LastNameInput } from "@/components/form-input/last-name-input";
import { PasswordInput } from "@/components/form-input/password-input";
import { PhoneInput } from "@/components/form-input/phone-input";
import { PostalAddressInput } from "@/components/form-input/postal-address-input";
import { PostalCodeInput } from "@/components/form-input/postal-code-input";
import type { ErrorField } from "@/generated/client.generated";
import { UserCreationErrorCode, userCreationErrorsToString } from "@/utils/error-code-lookup";
import dayjs from "dayjs";
import type Joi from "joi";
import { type Dispatch, type SetStateAction, useContext } from "react";
import { Heading } from "../../base/heading";
import { InputErrorMessage } from "../../base/input/input-error-message";
import { HorizontalSpacer } from "../../layout/horizontal-spacer";
import { ErrorMessageString } from "../../ui/error-message";
import { FormStateContext } from "../form-state-context";
import { validateOtherUserPassword } from "./validation";

interface OtherPayerProps {
  serverValidationErrors: ReadonlyArray<ErrorField>;
  validation: ReadonlyArray<Joi.ValidationErrorItem>;
  setValidation: Dispatch<SetStateAction<Joi.ValidationErrorItem[]>>;
  validateOnBlur: (fieldValue: string, fieldName: string) => Joi.ValidationErrorItem[] | undefined;
}
export const OtherPayer = ({ serverValidationErrors, validation, setValidation, validateOnBlur }: OtherPayerProps) => {
  const { formData, setFormData } = useContext(FormStateContext);
  return (
    <>
      <Heading type="h2">Kort om medlemmet</Heading>

      <InputGroup>
        <EmailInput
          externalValidationError={
            serverValidationErrors.find((error) => error.field === "otherEmail")?.errorCode ===
            "EMAIL_ALREADY_EXISTS" ? (
              <ErrorMessageString errorString="E-postadressen er allerede i bruk. Ta kontakt med kundeservice." />
            ) : (
              validation.find((d) => d.context?.label === "E-postadresse til medlem")?.message
            )
          }
          id="input-email-other"
          onBlur={() => validateOnBlur(formData.onBehalfOf.email, "onBehalfOf.email")}
          onChange={(e) => {
            setFormData({
              onBehalfOf: { ...formData.onBehalfOf, email: e.target.value },
            });
          }}
          value={formData.onBehalfOf.email}
        />
      </InputGroup>

      <HorizontalSpacer size={"medium"} />

      <InputGroup>
        <FirstNameInput
          externalValidationError={validation.find((d) => d.context?.label === "Fornavn til medlem")?.message}
          id="input-first-name-other"
          onBlur={() => validateOnBlur(formData.onBehalfOf.firstName, "onBehalfOf.firstName")}
          onChange={(e) =>
            setFormData({
              onBehalfOf: { ...formData.onBehalfOf, firstName: e.target.value },
            })
          }
          value={formData.onBehalfOf.firstName}
        />
        <HorizontalSpacer />

        <LastNameInput
          externalValidationError={validation.find((d) => d.context?.label === "Etternavn til medlem")?.message}
          id="input-last-name-other"
          onBlur={() => validateOnBlur(formData.onBehalfOf.lastName, "onBehalfOf.lastName")}
          onChange={(e) =>
            setFormData({
              onBehalfOf: { ...formData.onBehalfOf, lastName: e.target.value },
            })
          }
          value={formData.onBehalfOf.lastName}
        />

        <HorizontalSpacer />

        <PhoneInput
          externalValidationError={
            serverValidationErrors.find((error) => error.field === "otherMobilePhone.number")?.errorCode ===
            UserCreationErrorCode.PHONENUMBER_INVALID
              ? userCreationErrorsToString(UserCreationErrorCode.PHONENUMBER_INVALID)
              : validation?.find((error) => error.context?.label === "Telefonnummer til medlem")?.message
          }
          id="input-phone-other"
          onBlur={() => validateOnBlur(formData.onBehalfOf.phone, "onBehalfOf.phone")}
          onChange={(e) =>
            setFormData({
              onBehalfOf: { ...formData.onBehalfOf, phone: e.target.value },
            })
          }
          value={formData.onBehalfOf.phone}
        />

        <HorizontalSpacer />

        <DateInput
          id="input-birthdate-other"
          onBlur={() => validateOnBlur(formData.onBehalfOf.birthdate, "onBehalfOf.birthdate")}
          onChange={(date) =>
            setFormData({
              onBehalfOf: {
                ...formData.onBehalfOf,
                birthdate: dayjs(date).format("YYYY-MM-DD"),
              },
            })
          }
          showLabel={true}
          value={formData.onBehalfOf.birthdate}
        />

        {/* Siden DateInput er delt opp i 3 separate deler, viser vi ekstern feilmelding under feltet som helhet */}
        <InputErrorMessage
          errorMessage={undefined}
          externalErrorMessage={
            serverValidationErrors.find((error) => error.field === "otherBirthDate") &&
            !validation.some((d) => d.context?.label === "Fødselsdato til medlem") // ikke vis hvis det er intern valideringsfeil
              ? serverValidationErrors.find((error) => error.field === "otherBirthDate")?.errorCode
              : validation.find((d) => d.context?.label === "Fødselsdato til medlem")?.message
          }
        />
      </InputGroup>

      <HorizontalSpacer size={"medium"} />

      <InputGroup>
        <AddressInput
          externalValidationError={validation.find((d) => d.context?.label === "Adresse til medlem")?.message}
          id="input-address-other"
          onBlur={() => validateOnBlur(formData.onBehalfOf.address, "onBehalfOf.address")}
          onChange={(e) =>
            setFormData({
              onBehalfOf: { ...formData.onBehalfOf, address: e.target.value },
            })
          }
          value={formData.onBehalfOf.address ?? ""}
        />
        <HorizontalSpacer />

        <PostalCodeInput
          externalValidationError={validation.find((d) => d.context?.label === "Postnummer til medlem")?.message}
          id="input-postalcode-other"
          onBlur={() => validateOnBlur(formData.onBehalfOf.postalCode, "onBehalfOf.postalCode")}
          onChange={(e) =>
            setFormData({
              onBehalfOf: {
                ...formData.onBehalfOf,
                postalCode: e.target.value,
              },
            })
          }
          value={formData.onBehalfOf.postalCode}
        />

        <HorizontalSpacer />

        <PostalAddressInput
          externalValidationError={validation.find((d) => d.context?.label === "Poststed til medlem")?.message}
          id="input-postaladdress-other"
          onBlur={() => validateOnBlur(formData.onBehalfOf.postalAddress, "onBehalfOf.postalAddress")}
          onChange={(e) =>
            setFormData({
              onBehalfOf: {
                ...formData.onBehalfOf,
                postalAddress: e.target.value,
              },
            })
          }
          value={formData.onBehalfOf.postalAddress}
        />
      </InputGroup>

      <HorizontalSpacer size={"medium"} />

      <InputGroup>
        <HorizontalSpacer size={"x-small"} />
        <GenderInput
          externalErrorMessage={validation.find((d) => d.context?.label === "Kjønn medlem")?.message}
          idAll="gender-all-other"
          idFemale="gender-female-other"
          idMale="gender-male-other"
          checkedAll={formData.onBehalfOf.gender === "ønsker ikke å oppgi"}
          checkedFemale={formData.onBehalfOf.gender === "kvinne"}
          checkedMale={formData.onBehalfOf.gender === "mann"}
          name="Kjønn-other"
          onChange={(e) =>
            setFormData({
              onBehalfOf: { ...formData.onBehalfOf, gender: e.target.value },
            })
          }
        />
      </InputGroup>

      <HorizontalSpacer size="medium" />

      {!formData.otherUser?.userId && (
        <InputGroup>
          <Heading type="h2">Lag et passord for medlemmet</Heading>
          <HorizontalSpacer size={"small"} />
          <PasswordInput
            id="password-other"
            name="Passord til medlem"
            label="Passord til medlem"
            onChange={(e) =>
              setFormData({
                onBehalfOf: {
                  ...formData.onBehalfOf,
                  password: e.target.value,
                },
              })
            }
            onBlur={() =>
              validateOtherUserPassword(
                formData.onBehalfOf,
                setValidation,
                formData.onBehalfOf?.otherPayer,
                "Passord til medlem",
              )
            }
            value={formData.onBehalfOf.password}
          />
          <InputErrorMessage
            externalErrorMessage={validation.find((d) => d.context?.label === "Passord til medlem")?.message}
          />
          <HorizontalSpacer />
          <PasswordInput
            id="password-confirmation-other"
            name="Gjenta passord til medlem"
            label="Gjenta passord til medlem"
            onBlur={() =>
              validateOtherUserPassword(
                formData.onBehalfOf,
                setValidation,
                formData.onBehalfOf?.otherPayer,
                "Bekreft passord til medlem",
              )
            }
            onChange={(e) =>
              setFormData({
                onBehalfOf: {
                  ...formData.onBehalfOf,
                  passwordConfirmation: e.target.value,
                },
              })
            }
            value={formData.onBehalfOf.passwordConfirmation}
          />
          <InputErrorMessage
            externalErrorMessage={validation.find((d) => d.context?.label === "Bekreft passord til medlem")?.message}
          />
        </InputGroup>
      )}
    </>
  );
};
